import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Users, 
  Highlighter, 
  BookOpen, 
  PanelBottom, 
  LayoutGrid,
  CheckCircle,
  EyeOff,
  Wifi,
  Database,
  Fingerprint,
  Heart,
  Monitor,
  Smartphone
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import './SafePulseBrowser.css';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const features = [
  { icon: Brain, title: 'On-device AI Agent', description: 'Ask questions about any page, get summaries, extract concepts, and have natural conversations about what you\'re reading. The model runs locally — your prompts never leave your device.' },
  { icon: Users, title: 'Council of Agents', description: 'Spin up multiple specialised agents — Researcher, Critic, Coach, Writer, and any custom role you create. Configure each one\'s name, colour, icon, and behaviour.' },
  { icon: Highlighter, title: 'Highlighter Pens', description: 'Five colour-coded highlighter pens turn the web into your personal notebook. Highlight anything, then ask the agent about that exact passage with one tap.' },
  { icon: BookOpen, title: 'Notebook Bookmarks', description: 'Saved pages land in a ruled-paper notebook view alongside your colour-coded highlights, so research stays organised the way you actually think.' },
  { icon: PanelBottom, title: 'Inline Agent Sheet', description: 'Pull up the agent as a bottom sheet over the current page — no tab switching, no losing your place. Seamless AI assistance while you browse.' },
  { icon: LayoutGrid, title: 'Multi-tab & Speed Dial', description: 'Familiar tab management with a clean speed-dial home screen for the sites you visit most. Fast, intuitive, distraction-free.' }
];

const privacyChecks = [
  'No analytics or telemetry',
  'No advertising SDKs',
  'No third-party tracking',
  'No account required',
  'All data stored locally on device',
  'AI prompts never leave your device'
];

const privacyIcons = [
  { icon: EyeOff, label: 'No Tracking' },
  { icon: Wifi, label: 'No Cloud' },
  { icon: Database, label: 'Local Storage' },
  { icon: Fingerprint, label: 'No Accounts' }
];

const useCases = [
  {
    num: '1',
    title: 'Researchers & Students',
    description: 'Highlight key passages, ask the AI agent to summarise or critique, and keep everything organised in your notebook view.',
    benefits: ['Colour-coded highlighting', 'AI-powered summarisation', 'Notebook bookmarks']
  },
  {
    num: '2',
    title: 'Privacy-Conscious Users',
    description: 'Browse without being tracked, profiled, or targeted. Everything runs locally — your data never leaves your device.',
    benefits: ['Zero data collection', 'No cloud dependencies', 'Complete data ownership']
  },
  {
    num: '3',
    title: 'Knowledge Workers',
    description: 'Use specialised AI agents to help with writing, analysis, and decision-making — all without leaving your browser.',
    benefits: ['Custom agent roles', 'Inline AI assistance', 'Multi-tab workflows']
  }
];

const SafePulseBrowser = () => {

  return (
    <div className="spb-page">
      <Helmet>
        <title>SafePulse Browser - Erasys</title>
        <meta
          name="description"
          content="SafePulse Browser: A private, local-first browser with a built-in AI agent that runs entirely on your device. No cloud, no tracking, free forever."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="spb-hero-section tech-bg">
        <div className="spb-hero-content">
          <motion.h1
            className="spb-hero-title"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            SafePulse Browser
          </motion.h1>
          <motion.p
            className="spb-hero-tagline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
          >
            Your AI. Your Device. Your Privacy.
          </motion.p>
          <motion.div
            className="spb-links-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: 'easeOut' }}
          >
            <div className="spb-hero-buttons">
              <a 
                href="https://apps.apple.com/us/app/safepulse-browser/id6768818859" 
                target="_blank" 
                rel="noopener noreferrer"
                className="spb-secondary-link"
              >
                <Smartphone size={18} />
                iOS
                <span className="spb-link-arrow">→</span>
              </a>
              <a 
                href="https://cdn.erasys.co.uk/installers/SafePulse browser v1.56.zip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="spb-secondary-link"
              >
                <Monitor size={18} />
                macOS
                <span className="spb-link-arrow">→</span>
              </a>
              <a 
                href="https://cdn.erasys.co.uk/safepulsebrowser/SafePulse Browser Setup 1.0.5.exe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="spb-secondary-link"
              >
                <Monitor size={18} />
                Windows
                <span className="spb-link-arrow">→</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero Image Showcase */}
      <section className="spb-hero-showcase">
        <div className="spb-showcase-container">
          <div className="spb-showcase-content">
            <motion.div
              className="spb-showcase-text"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <div className="spb-section-label">Overview</div>
              <h2 className="spb-showcase-title">Browse Smarter, Stay Private</h2>
              <p className="spb-showcase-description">
                SafePulse is a private, local-first browser with a built-in AI agent that runs entirely on your device. 
                Browse the web with the help of a personal AI assistant — and a council of specialised agents — without 
                sending a single query to the cloud. No accounts. No tracking. No subscription. Ever.
              </p>
            </motion.div>
            <motion.div
              className="spb-showcase-image"
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <img src="/SafePulse 1.png" alt="SafePulse Browser" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Stats Section */}
      <section className="spb-feature-section">
        <motion.div
          className="spb-feature-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          <h2 className="spb-feature-section-title">Why SafePulse Browser</h2>
          <p className="spb-feature-section-subtitle">A smarter browser that respects your privacy</p>
        </motion.div>
        <motion.div
          className="spb-feature-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {[
            { stat: '100%', label: 'On-Device AI', desc: 'Every AI query runs locally on your device. Zero data sent to any server, ever.' },
            { stat: '5', label: 'Highlighter Pens', desc: 'Colour-coded highlighting turns the web into your personal research notebook.' },
            { stat: '∞', label: 'Custom Agents', desc: 'Create unlimited specialised AI agents — Researcher, Critic, Coach, Writer, and more.' },
            { stat: 'Free', label: 'Forever', desc: 'No subscription, no in-app purchases, no paywalled features. The full product, free.' }
          ].map((item, i) => (
            <motion.div key={i} className="spb-feature-item" variants={fadeUp} custom={i}>
              <h1>{item.stat}</h1>
              <h3>{item.label}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Features Detail Section */}
      <section className="spb-benefits-section">
        <div className="spb-benefits-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="spb-section-label-center">Features</div>
            <h2 className="spb-benefits-title">Everything You Need</h2>
            <p className="spb-benefits-subtitle">
              Powerful features designed for private, intelligent browsing
            </p>
          </motion.div>
          <motion.div
            className="spb-benefits-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div key={i} className="spb-benefit-card" variants={fadeUp} custom={i}>
                  <div className="spb-benefit-icon-wrapper">
                    <div className="spb-benefit-icon">
                      <Icon size={36} color="white" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="spb-benefit-title">{feature.title}</h3>
                  <p className="spb-benefit-description">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="spb-privacy-section">
        <div className="spb-privacy-container">
          <div className="spb-privacy-content">
            <motion.div
              className="spb-privacy-text-content"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <div className="spb-section-label">Privacy</div>
              <h2 className="spb-privacy-title">Privacy by Design</h2>
              <p className="spb-privacy-description">
                Everything stays on your device. SafePulse uses no analytics, no advertising SDKs, no telemetry, 
                and no third-party tracking. Bookmarks, highlights, and chat history live in local storage you control.
              </p>
              <ul className="spb-privacy-list">
                {privacyChecks.map((text, i) => (
                  <motion.li
                    key={i}
                    className="spb-privacy-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 * i, ease: 'easeOut' }}
                  >
                    <CheckCircle size={24} color="#34C759" />
                    <span>{text}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="spb-privacy-icons-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              {privacyIcons.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={i} className="spb-privacy-icon-card" variants={fadeUp} custom={i}>
                    <Icon size={32} />
                    <span>{item.label}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="spb-use-cases">
        <div className="spb-cases-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="spb-section-label-center-light">Use Cases</div>
            <h2 className="spb-cases-title">Built For</h2>
            <p className="spb-cases-subtitle">Anyone who values privacy and productivity</p>
          </motion.div>
          <motion.div
            className="spb-cases-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {useCases.map((uc, i) => (
              <motion.div key={i} className="spb-case-card" variants={fadeUp} custom={i}>
                <div className="spb-case-number">{uc.num}</div>
                <h3 className="spb-case-title">{uc.title}</h3>
                <p className="spb-case-description">{uc.description}</p>
                {uc.benefits.map((b, j) => (
                  <div key={j} className="spb-case-benefit">{b}</div>
                ))}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="spb-platform-section">
        <motion.div
          className="spb-platform-content"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          <motion.div
            className="spb-platform-icon"
            initial={{ scale: 0, rotate: -20 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Heart size={48} />
          </motion.div>
          <h2>Free Forever. No Catch.</h2>
          <p>No subscription. No in-app purchases. No paywalled features. SafePulse Browser is a free app — that's the whole product.</p>
          <motion.div
            className="spb-platform-buttons"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a 
              href="https://apps.apple.com/us/app/safepulse-browser/id6768818859" 
              target="_blank" 
              rel="noopener noreferrer"
              className="spb-platform-btn"
            >
              <Smartphone size={20} />
              <span>Download for iOS</span>
            </a>
            <a 
              href="https://cdn.erasys.co.uk/installers/SafePulse Browser V1.0.5.dmg" 
              target="_blank" 
              rel="noopener noreferrer"
              className="spb-platform-btn spb-platform-btn-mac"
            >
              <Monitor size={20} />
              <span>Download for macOS</span>
            </a>
            <a 
              href="https://cdn.erasys.co.uk/safepulsebrowser/SafePulse Browser Setup 1.0.5.exe" 
              target="_blank" 
              rel="noopener noreferrer"
              className="spb-platform-btn spb-platform-btn-win"
            >
              <Monitor size={20} />
              <span>Download for Windows</span>
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default SafePulseBrowser;
